/**
 * Word lookups for the building grammar: per word form its parts of speech, noun gender, lemma, one IPA (with the
 * dialect tag it came from) and, for Persian, a romanisation. Reduced from Wiktionary (https://en.wiktionary.org,
 * via kaikki.org wiktextract), CC BY-SA 4.0, by the server's index builder (server/src/lexicon-build.ts, v1 ticket
 * 13). The server stores each item's entries with the item and sends the town's words with GET /api/town;
 * home-town.ts registers them for the town's language (the prototype registers its demo words). With none
 * registered, or for a word it does not know, the grammar keeps its spelling rules.
 */
import type { WordClass } from './building-grammar';
import { caNorm, faNorm, faStems, faWords } from '../lexicon-keys.generated';

export interface LexEntry {
  /** Wiktionary parts of speech (verb, noun, adj, adv, pron, …). */
  pos: string[];
  /** Gender of a noun (or of an adjective form). */
  g?: ('m' | 'f')[];
  /** The lemma(s) when this form is an inflection or variant (at most three). */
  lemma?: string[];
  /** Base words it is built on, from its etymology (affix or compound parts; Persian: √ and an Arabic root). */
  base?: string[];
  /**
   * One pronunciation, brackets kept, and its dialect tag (Catalan North-Western first; Persian Iranian; English
   * General American; Spanish Spain).
   */
  ipa?: string;
  dia?: string;
  /** Persian: romanisation (Iranian, â for the long a). Conjugation-table forms carry the stress as an acute. */
  rom?: string;
}
export interface Lexicon { _source?: string; language: string; words: Record<string, LexEntry> }

const registry = new Map<string, Lexicon>();
const romIndexes = new Map<string, Set<string>>();

let version = 0;
/** Changes whenever a lookup is registered or cleared, so the looks' blueprint caches know to start again. */
export const lexiconVersion = (): number => version;

/** Registers (or with null clears) the word lookup for one target language. */
export function setLexicon(language: string, lex: Lexicon | null): void {
  if (lex) registry.set(language, lex); else registry.delete(language);
  romIndexes.delete(language);
  version++;
}
export function clearLexicons(): void { registry.clear(); romIndexes.clear(); version++; }
export const hasLexicon = (language: string): boolean => registry.has(language);

// ---- normalisation: one source with the server (server/src/lexicon-keys.ts) --------------------------------------

export { caNorm, enContraction, faNorm, faStems, faWords, phraseKey } from '../lexicon-keys.generated';

export function lookup(language: string, word: string): LexEntry | undefined {
  const words = registry.get(language)?.words;
  return words?.[language === 'fa' ? faNorm(word) : caNorm(word)];
}
/** The entries of this form's lemmas that the lookup holds. */
export const lemmasOf = (language: string, e: LexEntry): LexEntry[] =>
  (e.lemma ?? []).map((l) => lookup(language, l)).filter((x): x is LexEntry => !!x);

// ---- word class ----------------------------------------------------------------------------------------------

const NOUNISH = new Set(['noun', 'name', 'proper noun']);
/** Parts of speech → the grammar's three classes, in the lookup's order (verb; noun; everything else other). */
export function classesOf(e: LexEntry): WordClass[] {
  const out: WordClass[] = [];
  for (const p of e.pos) {
    const c: WordClass = p === 'verb' ? 'verb' : NOUNISH.has(p) ? 'noun' : 'other';
    if (!out.includes(c)) out.push(c);
  }
  return out;
}
/** Can be a verb and cannot be a noun or adjective (so el/la/l' in front of it is a pronoun). */
export const verbNotNominal = (e: LexEntry | undefined): boolean =>
  !!e && e.pos.includes('verb') && !e.pos.some((p) => NOUNISH.has(p) || p === 'adj' || p === 'num');

/** What the gloss says for sure: "to …" a verb, "the/a/an …" a noun, "I/you/she … <word>" a verb; else nothing. */
export function glossHint(en: string): WordClass | null {
  const e = en.trim().toLowerCase().replace(/^[("'“‘«¡¿\s]+/, '');
  if (/^to\s+\p{L}/u.test(e)) return 'verb';
  if (/^(the|an?)\s+\p{L}/u.test(e)) return 'noun';
  if (/^(i|you|he|she|we|they)\s+\p{L}/u.test(e)) return 'verb';
  return null;
}
/** A gloss that reads as a clause ("what do you want?", "it is hot", "to go there"). */
export const clauseGloss = (en: string): boolean =>
  /^\W*(to|i|you|he|she|it|we|they|what|where|how|when|who|why|do|does|did|can|could|would|will|let's)\b|\b(is|are|was|were|do|does|did|don't|doesn't|can't|won't|am)\b/i.test(en);

/**
 * One class from the lookup's options: the only one; else what the gloss says for sure, if allowed; else the
 * spelling-rule guess (`today`), if allowed; else the lookup's first option.
 */
export function pickClass(options: WordClass[], en: string, today: WordClass): WordClass {
  if (options.length === 1) return options[0];
  const hint = glossHint(en);
  if (hint && options.includes(hint)) return hint;
  return options.includes(today) ? today : options[0];
}

// ---- IPA -----------------------------------------------------------------------------------------------------

/** The transcription without its brackets; null when it has optional parts in parentheses (no firm answer). */
export function ipaCore(ipa: string): string | null {
  const s = ipa.trim().replace(/^[/[]|[/\]]$/g, '');
  return /[()]/.test(s) ? null : s;
}
/** The sounds only: no stress, syllable, length or linking marks. */
export const ipaSounds = (ipa: string): string | null => ipaCore(ipa)?.replace(/[ˈˌ.ː‿\s]/g, '') ?? null;

/**
 * Syllables in a transcription and which one is stressed: ˈ opens it, or (Persian, Iranian) an acute sits on its
 * vowel. stress null = the transcription does not mark it.
 */
export function ipaSyllables(ipa: string): { n: number; stress: number | null } | null {
  const core = ipaCore(ipa);
  if (core === null) return null;
  const parts = core.normalize('NFD').replace(/ˈ/g, '.ˈ').replace(/ˌ/g, '.').split(/[.\s‿]+/).filter((p) => /\p{L}/u.test(p));
  if (!parts.length) return null;
  const k = parts.findIndex((p) => p.startsWith('ˈ') || p.includes('́'));
  return { n: parts.length, stress: k >= 0 ? k : parts.length === 1 ? 0 : null };
}

// ---- Persian: script words, Finglish ↔ romanisation -----------------------------------------------------------

/** A Persian word's entry: as written, else without its written ezafe, else its stem before a joiner. */
export function lookupFa(word: string): LexEntry | undefined {
  const e = lookup('fa', word);
  if (e) return e;
  for (const k of faStems(word)) { const x = lookup('fa', k); if (x) return x; }
  return undefined;
}
/** The script marks an ezafe on this word: ه‌ی, ۀ, a kasra or a hamza above. */
export const faEzafeMark = (w: string): boolean => /(ه‌ی|ۀ|[ِٔ])$/.test(w.normalize('NFC'));
/** A verb by its spelling: the present prefix می‌ / نمی‌ written apart (joiner or space). */
export const faVerbSpelling = (w: string): boolean => /^ن?می‌/.test(w.normalize('NFC'));

/** Is this Persian form a verb? From the lookup; null when it can be either (خونه: house, or "reads"). */
export function faVerb(e: LexEntry): boolean | null {
  if (!e.pos.includes('verb')) return false;
  if (e.pos.every((p) => p === 'verb')) return true;
  const ls = lemmasOf('fa', e);
  return ls.length && ls.every((l) => l.pos.includes('verb')) ? true : null; // بخور ← خوردن
}

/** Finglish as learners type it → a comparable key: â for aa/ā/á, x kh, š sh, č ch, ž zh, ğ gh/q, u oo, i ee. */
export function finglishKey(s: string): string {
  return s.toLowerCase().normalize('NFC').replace(/aa|[āáà]/g, 'â').replace(/kh/g, 'x').replace(/sh/g, 'š').replace(/ch/g, 'č')
    .replace(/zh/g, 'ž').replace(/gh|q/g, 'ğ').replace(/oo/g, 'u').replace(/ee/g, 'i').replace(/c(?=[eiy])/g, 's')
    .replace(/eh$/, 'e').replace(/['’-]/g, '');
}
/** A Wiktionary romanisation → the same key (the acute is stress, a macron length, y between i and a vowel a glide). */
export function romKey(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/ā/g, 'â').replace(/[̀́̄̆]/g, '').normalize('NFC')
    .replace(/q/g, 'ğ').replace(/iy(?=[aâeiou])/g, 'i').replace(/['’ʼ-]/g, '');
}
/** Consonants only (w as v; h, glides and the glottal stop dropped; doubles collapsed). */
const skeleton = (key: string) => key.replace(/w/g, 'v').replace(/[aâeiouyhʔ']/g, '').replace(/(.)\1+/g, '$1');
function distance(a: string, b: string): number {
  let row = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const next = [i];
    for (let j = 1; j <= b.length; j++) next[j] = Math.min(row[j] + 1, next[j - 1] + 1, row[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    row = next;
  }
  return row[b.length];
}
/** Could a Finglish word and a romanisation be one word? Same first consonant, skeletons at most 1 apart (2 if long). */
export function sameWord(finglish: string, rom: string): boolean {
  const a = skeleton(finglishKey(finglish)), b = skeleton(romKey(rom.split(/[,\s]/)[0]));
  if (!a || !b) return a === b;
  return a[0] === b[0] && distance(a, b) <= (Math.max(a.length, b.length) > 4 ? 2 : 1);
}

/**
 * Pairs each Finglish word with the Persian-script word in the same position, when both count the same words and
 * every pair whose romanisation is known looks like one word. Else null (the spelling rules decide).
 */
export function alignFa(finglish: string[], script: string): string[] | null {
  if (!registry.has('fa')) return null;
  const fa = faWords(script);
  if (fa.length !== finglish.length) return null;
  for (let i = 0; i < fa.length; i++) {
    const rom = lookupFa(fa[i])?.rom;
    if (rom && !sameWord(finglish[i], rom)) return null;
  }
  return fa;
}

function romIndex(): Set<string> {
  let ix = romIndexes.get('fa');
  if (!ix) {
    ix = new Set(Object.values(registry.get('fa')?.words ?? {}).flatMap((e) => (e.rom ? [romKey(e.rom)] : [])));
    romIndexes.set('fa', ix);
  }
  return ix;
}
/**
 * An ezafe written onto the word (ketâbe man, khuneye man) → [stem, suffix], when the Finglish word ends in -e/-ye
 * and the word without it is the one the lookup knows: the script word beside it marks the ezafe or romanises as
 * the stem; with no script word, the stem is a known romanisation and the whole word is not. Else null.
 */
export function ezafeSplit(finglish: string, fa: string | undefined): [string, string] | null {
  if (!registry.has('fa')) return null;
  const m = /^(.{2,}[aâeiou])(ye)$/i.exec(finglish) ?? /^(.{2,}[^aâeiou'])(e)$/i.exec(finglish);
  if (!m) return null;
  const [, stem, suffix] = m;
  const whole = finglishKey(finglish), bare = finglishKey(stem);
  if (fa !== undefined) {
    if (faEzafeMark(fa)) return [stem, suffix];
    const rom = lookupFa(fa)?.rom;
    return rom && romKey(rom) === bare && romKey(rom) !== whole ? [stem, suffix] : null;
  }
  const ix = romIndex();
  return ix.has(bare) && !ix.has(whole) ? [stem, suffix] : null;
}
